import { useId } from "react";
import { Link } from "react-router-dom";

/* Faceted shield + star mark. Pure SVG so it is crisp at every size. */
export const LogoMark = ({ size = 36 }) => {
  const id = useId().replace(/:/g, "");
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <defs>
        <linearGradient id={`${id}-a`} x1="8" y1="2" x2="40" y2="46" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#c4b5fd" />
          <stop offset="0.45" stopColor="#8b5cf6" />
          <stop offset="1" stopColor="#3b0f8c" />
        </linearGradient>
        <linearGradient id={`${id}-b`} x1="24" y1="3" x2="24" y2="45" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${id}-s`} x1="24" y1="14" x2="24" y2="33" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fff8e6" />
          <stop offset="1" stopColor="#f6d58e" />
        </linearGradient>
      </defs>
      {/* extrusion */}
      <path d="M24 5 40 11v12c0 10-7 17.5-16 21C15 40.5 8 33 8 23V11L24 5Z" transform="translate(0 2)" fill="#1e0b4d" />
      {/* body */}
      <path d="M24 3 40 9v12c0 10-7 17.5-16 21C15 38.5 8 31 8 21V9L24 3Z" fill={`url(#${id}-a)`} />
      {/* facet split: right half slightly darker */}
      <path d="M24 3 40 9v12c0 10-7 17.5-16 21V3Z" fill="#000" opacity="0.18" />
      {/* top light */}
      <path d="M24 3 40 9v12c0 10-7 17.5-16 21C15 38.5 8 31 8 21V9L24 3Z" fill={`url(#${id}-b)`} />
      <path d="M24 3 40 9v12c0 10-7 17.5-16 21C15 38.5 8 31 8 21V9L24 3Z" fill="none" stroke="#fff" strokeOpacity="0.35" strokeWidth="0.8" />
      {/* star */}
      <path
        d="m24 14.2 2.55 5.17 5.7.83-4.13 4.02.98 5.68L24 27.22l-5.1 2.68.98-5.68-4.13-4.02 5.7-.83L24 14.2Z"
        fill={`url(#${id}-s)`}
        stroke="#7a4f12"
        strokeOpacity="0.35"
        strokeWidth="0.6"
      />
    </svg>
  );
};

export const Brand = ({ to = "/", size = 34, className = "" }) => (
  <Link to={to} className={`brand ${className}`} aria-label="RateGuard">
    <LogoMark size={size} />
    <span className="brand-word">
      Rate<span>Guard</span>
    </span>
  </Link>
);

/* Gem-cut star used for ratings. `lit` switches between the champagne
   lit state and an empty glass outline. */
export const GemStar = ({ lit = false }) => {
  const id = useId().replace(/:/g, "");
  const d =
    "M32 4.5l8.3 16.8 18.5 2.7-13.4 13.1 3.2 18.4L32 46.8l-16.6 8.7 3.2-18.4L5.2 24l18.5-2.7L32 4.5Z";
  if (!lit) {
    return (
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <defs>
          <linearGradient id={`${id}-e`} x1="32" y1="4" x2="32" y2="56" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#3a2f56" />
            <stop offset="1" stopColor="#15101f" />
          </linearGradient>
        </defs>
        <path d={d} fill={`url(#${id}-e)`} stroke="#6d5f91" strokeWidth="1.4" strokeLinejoin="round" />
        <path d="M32 4.5l8.3 16.8L32 32V4.5Z" fill="#fff" opacity="0.05" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={`${id}-f`} x1="18" y1="4" x2="46" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fffaf0" />
          <stop offset="0.35" stopColor="#f6d58e" />
          <stop offset="1" stopColor="#b9822a" />
        </linearGradient>
      </defs>
      <path d={d} transform="translate(0 2.5)" fill="#7a4f12" opacity="0.9" />
      <path d={d} fill={`url(#${id}-f)`} stroke="#fff4d6" strokeOpacity="0.7" strokeWidth="1" strokeLinejoin="round" />
      {/* facets: center-to-tip planes for a cut-gem look */}
      <path d="M32 4.5 40.3 21.3 32 32Z" fill="#fff" opacity="0.35" />
      <path d="M5.2 24 23.7 21.3 32 32Z" fill="#fff" opacity="0.22" />
      <path d="M58.8 24 45.4 37.1 32 32Z" fill="#000" opacity="0.12" />
      <path d="M48.6 55.5 32 46.8 32 32Z" fill="#000" opacity="0.16" />
      <path d="M15.4 55.5 18.6 37.1 32 32Z" fill="#000" opacity="0.06" />
    </svg>
  );
};

export const LANGS = ["tr", "en", "sr"];

export const LangToggle = ({ lang, onChange, testId = "language-toggle", langs = LANGS }) => (
  <div
    className="panel-flat inline-flex p-1 gap-1"
    role="group"
    aria-label="Language"
    data-testid={testId}
    style={{ borderRadius: 12 }}
  >
    {langs.map((code) => (
      <button
        key={code}
        type="button"
        onClick={() => onChange(code)}
        aria-pressed={lang === code}
        className="mono"
        style={{
          minWidth: 36,
          height: 32,
          borderRadius: 9,
          border: 0,
          cursor: "pointer",
          fontSize: 12,
          letterSpacing: "0.12em",
          fontWeight: 600,
          color: lang === code ? "#fff" : "var(--text-3)",
          background:
            lang === code
              ? "linear-gradient(180deg, #8b5cf6, #5b21b6)"
              : "transparent",
          boxShadow:
            lang === code ? "inset 0 1px 0 rgba(255,255,255,.35), 0 4px 12px -4px rgba(124,58,237,.8)" : "none",
        }}
      >
        {code.toUpperCase()}
      </button>
    ))}
  </div>
);

export const LoadingScreen = () => (
  <div className="scene grid place-items-center" style={{ minHeight: "100vh" }}>
    <div className="flex flex-col items-center gap-4">
      <div className="bob">
        <LogoMark size={56} />
      </div>
      <div className="eyebrow faint">RateGuard</div>
    </div>
  </div>
);

export const detectLang = () => {
  try {
    const saved = localStorage.getItem("rg-lang");
    if (LANGS.includes(saved)) {
      applyDocLang(saved);
      return saved;
    }
  } catch (e) {
    /* storage unavailable */
  }
  const nav = (typeof navigator !== "undefined" && navigator.language) || "tr";
  const n = nav.toLowerCase();
  const lang = n.startsWith("tr") ? "tr" : /^(sr|hr|bs|me|cnr)/.test(n) ? "sr" : "en";
  applyDocLang(lang);
  return lang;
};

/* Keep <html lang> in sync: CSS uppercase of "i" differs between tr and en. */
export const applyDocLang = (lang) => {
  if (typeof document !== "undefined") document.documentElement.lang = lang;
};

export const saveLang = (lang) => {
  applyDocLang(lang);
  try {
    localStorage.setItem("rg-lang", lang);
  } catch (e) {
    /* storage unavailable */
  }
};
